import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "budget-suga-info-panel" }
const _hoisted_2 = { class: "budget-suga-info-toolbar" }
const _hoisted_3 = { class: "budget-suga-info-toolbar__row" }
const _hoisted_4 = { class: "budget-suga-info-toolbar__row budget-suga-info-toolbar__row--copy" }
const _hoisted_5 = { class: "budget-suga-info-table-wrap" }
const _hoisted_6 = { class: "budget-suga-info-table" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { class: "tar" }
const _hoisted_9 = { class: "tar" }
const _hoisted_10 = { class: "tar" }
const _hoisted_11 = { class: "budget-suga-info-table__actions" }
const _hoisted_12 = { class: "budget-suga-info-add" }

import { ref, computed, onMounted } from "vue";
import { budgetApi } from "@/services/api";

export interface SugaInfoRow {
  csi_idx: number;
  csi_biz_gubun: string;
  csi_year: number;
  csi_is_use: string;
  csi_rank: string;
  csi_basis_yn: string;
  csi_gross_fee: number;
  csi_copay_fee: number;
  csi_sort: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BudgetSugaInfoModal',
  setup(__props) {

const bizOptions = [
  "요양시설",
  "공동생활가정",
  "주야간보호",
  "방문요양",
  "방문목욕",
  "방문간호",
  "복지용구",
];

const thisYear = new Date().getFullYear();
const loading = ref(false);
const copying = ref(false);
const rows = ref<SugaInfoRow[]>([]);
const yearsFromApi = ref<number[]>([]);
const editYear = ref<number>(thisYear);
const copyFromYear = ref<number | null>(null);
const copyToYear = ref<number>(thisYear + 1);

const yearItems = computed(() => {
  const set = new Set<number>(yearsFromApi.value);
  set.add(thisYear);
  set.add(editYear.value);
  return Array.from(set).sort((a, b) => b - a);
});

const formOpen = ref(false);
const formMode = ref<"add" | "edit">("add");
const formSaving = ref(false);
const editingIdx = ref<number | null>(null);
const form = ref({
  bizGubun: "",
  year: editYear.value,
  rank: "",
  isUse: "Y" as "Y" | "N",
  basisYn: "N" as "Y" | "N",
  grossFee: "",
  copayFee: "",
  sort: 10,
});

function formatNum(n: number) {
  return Number(n || 0).toLocaleString();
}

async function loadYears() {
  try {
    const res = await budgetApi.getSugaInfoYears();
    const d = res.data as { success?: boolean; data?: number[] };
    if (d?.success && Array.isArray(d.data)) {
      yearsFromApi.value = d.data.map((x) => Number(x));
    }
  } catch {
    yearsFromApi.value = [];
  }
}

async function loadList() {
  loading.value = true;
  try {
    const res = await budgetApi.getSugaInfoList({ year: editYear.value });
    const d = res.data as { success?: boolean; data?: SugaInfoRow[] };
    rows.value = d?.success && Array.isArray(d.data) ? d.data : [];
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } } };
    alert(err.response?.data?.message || "목록을 불러오지 못했습니다.");
    rows.value = [];
  } finally {
    loading.value = false;
  }
}

async function doCopyYear() {
  const fy = copyFromYear.value;
  const ty = Number(copyToYear.value);
  if (fy == null || Number.isNaN(ty)) {
    alert("원본 연도와 대상 연도를 입력하세요.");
    return;
  }
  if (!confirm(`${fy}년 틀을 ${ty}년으로 복사합니다. 급여·본인부담은 0으로 둡니다. 계속할까요?`)) return;
  copying.value = true;
  try {
    const res = await budgetApi.copySugaInfoYear({ fromYear: fy, toYear: ty });
    const d = res.data as { success?: boolean; message?: string; inserted?: number };
    if (d?.success) {
      await loadYears();
      editYear.value = ty;
      await loadList();
      alert(`${d.inserted ?? 0}건 복사되었습니다.`);
    } else {
      alert(d?.message || "복사에 실패했습니다.");
    }
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string }; status?: number } };
    alert(err.response?.data?.message || "복사 중 오류가 발생했습니다.");
  } finally {
    copying.value = false;
  }
}

function openAdd() {
  formMode.value = "add";
  editingIdx.value = null;
  form.value = {
    bizGubun: bizOptions[0],
    year: editYear.value,
    rank: "",
    isUse: "Y",
    basisYn: "N",
    grossFee: "",
    copayFee: "",
    sort: 10,
  };
  formOpen.value = true;
}

function openEdit(r: SugaInfoRow) {
  formMode.value = "edit";
  editingIdx.value = r.csi_idx;
  form.value = {
    bizGubun: r.csi_biz_gubun,
    year: r.csi_year,
    rank: r.csi_rank,
    isUse: r.csi_is_use === "N" ? "N" : "Y",
    basisYn: r.csi_basis_yn === "Y" ? "Y" : "N",
    grossFee: String(r.csi_gross_fee ?? ""),
    copayFee: String(r.csi_copay_fee ?? ""),
    sort: r.csi_sort,
  };
  formOpen.value = true;
}

async function submitForm() {
  formSaving.value = true;
  try {
    if (formMode.value === "add") {
      const res = await budgetApi.postSugaInfo({
        bizGubun: form.value.bizGubun,
        year: form.value.year,
        rank: form.value.rank,
        isUse: form.value.isUse,
        basisYn: form.value.basisYn,
        grossFee: form.value.grossFee,
        copayFee: form.value.copayFee,
        sort: form.value.sort,
      });
      const d = res.data as { success?: boolean; message?: string };
      if (!d?.success) {
        alert(d?.message || "저장 실패");
        return;
      }
    } else if (editingIdx.value != null) {
      const res = await budgetApi.patchSugaInfo(editingIdx.value, {
        bizGubun: form.value.bizGubun,
        year: form.value.year,
        rank: form.value.rank,
        isUse: form.value.isUse,
        basisYn: form.value.basisYn,
        grossFee: form.value.grossFee,
        copayFee: form.value.copayFee,
        sort: form.value.sort,
      });
      const d = res.data as { success?: boolean; message?: string };
      if (!d?.success) {
        alert(d?.message || "수정 실패");
        return;
      }
    }
    formOpen.value = false;
    await loadYears();
    await loadList();
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } } };
    alert(err.response?.data?.message || "저장 중 오류");
  } finally {
    formSaving.value = false;
  }
}

async function confirmDelete(r: SugaInfoRow) {
  if (!confirm(`삭제할까요?\n${r.csi_biz_gubun} / ${r.csi_rank}`)) return;
  try {
    const res = await budgetApi.deleteSugaInfo(r.csi_idx);
    const d = res.data as { success?: boolean; message?: string };
    if (!d?.success) {
      alert(d?.message || "삭제 실패");
      return;
    }
    await loadList();
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } } };
    alert(err.response?.data?.message || "삭제 중 오류");
  }
}

onMounted(async () => {
  await loadYears();
  if (copyFromYear.value == null && yearItems.value.length) {
    copyFromYear.value = yearItems.value[0];
  }
  await loadList();
});

return (_ctx: any,_cache: any) => {
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_v_card, { class: "budget-suga-info-card" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_text, { class: "budget-suga-info-card__body" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _cache[15] || (_cache[15] = _createElementVNode("label", { class: "budget-suga-info-label" }, "편집 연도", -1)),
                  _createVNode(_component_v_select, {
                    modelValue: editYear.value,
                    "onUpdate:modelValue": [
                      _cache[0] || (_cache[0] = ($event: any) => ((editYear).value = $event)),
                      loadList
                    ],
                    items: yearItems.value,
                    density: "compact",
                    variant: "outlined",
                    "hide-details": "",
                    class: "budget-suga-info-field budget-suga-info-field--year"
                  }, null, 8, ["modelValue", "items"]),
                  _createVNode(_component_v_btn, {
                    variant: "flat",
                    class: "btn-refresh",
                    loading: loading.value,
                    onClick: loadList
                  }, {
                    default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                      _createTextVNode("새로고침", -1)
                    ]))]),
                    _: 1
                  }, 8, ["loading"])
                ]),
                _createElementVNode("div", _hoisted_4, [
                  _cache[17] || (_cache[17] = _createElementVNode("span", { class: "budget-suga-info-copy-label" }, "다음 연도 틀 복사 (급여·본인부담 0)", -1)),
                  _createVNode(_component_v_select, {
                    modelValue: copyFromYear.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((copyFromYear).value = $event)),
                    items: yearItems.value,
                    density: "compact",
                    variant: "outlined",
                    "hide-details": "",
                    placeholder: "원본 연도",
                    class: "budget-suga-info-field budget-suga-info-field--sm"
                  }, null, 8, ["modelValue", "items"]),
                  _cache[18] || (_cache[18] = _createElementVNode("span", { class: "budget-suga-info-arrow" }, "→", -1)),
                  _createVNode(_component_v_text_field, {
                    modelValue: copyToYear.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((copyToYear).value = $event)),
                    modelModifiers: { number: true },
                    type: "number",
                    density: "compact",
                    variant: "outlined",
                    "hide-details": "",
                    class: "budget-suga-info-field budget-suga-info-field--xs"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_v_btn, {
                    variant: "flat",
                    class: "btn-confirm",
                    loading: copying.value,
                    onClick: doCopyYear
                  }, {
                    default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                      _createTextVNode("복사", -1)
                    ]))]),
                    _: 1
                  }, 8, ["loading"])
                ])
              ]),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("table", _hoisted_6, [
                  _cache[22] || (_cache[22] = _createElementVNode("thead", null, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", null, "사업구분"),
                      _createElementVNode("th", null, "구간"),
                      _createElementVNode("th", null, "사용"),
                      _createElementVNode("th", null, "기초"),
                      _createElementVNode("th", null, "급여총액"),
                      _createElementVNode("th", null, "본인부담"),
                      _createElementVNode("th", null, "순서"),
                      _createElementVNode("th", { class: "budget-suga-info-table__actions" }, "관리")
                    ])
                  ], -1)),
                  _createElementVNode("tbody", null, [
                    (!loading.value && rows.value.length === 0)
                      ? (_openBlock(), _createElementBlock("tr", _hoisted_7, [...(_cache[19] || (_cache[19] = [
                          _createElementVNode("td", {
                            colspan: "8",
                            class: "budget-suga-info-table__empty"
                          }, "데이터가 없습니다.", -1)
                        ]))]))
                      : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (r) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: r.csi_idx
                      }, [
                        _createElementVNode("td", null, _toDisplayString(r.csi_biz_gubun), 1),
                        _createElementVNode("td", null, _toDisplayString(r.csi_rank), 1),
                        _createElementVNode("td", null, _toDisplayString(r.csi_is_use), 1),
                        _createElementVNode("td", null, _toDisplayString(r.csi_basis_yn), 1),
                        _createElementVNode("td", _hoisted_8, _toDisplayString(formatNum(r.csi_gross_fee)), 1),
                        _createElementVNode("td", _hoisted_9, _toDisplayString(formatNum(r.csi_copay_fee)), 1),
                        _createElementVNode("td", _hoisted_10, _toDisplayString(r.csi_sort), 1),
                        _createElementVNode("td", _hoisted_11, [
                          _createVNode(_component_v_btn, {
                            icon: "",
                            size: "x-small",
                            variant: "text",
                            color: "primary",
                            onClick: ($event: any) => (openEdit(r))
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_icon, { size: "18" }, {
                                default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                                  _createTextVNode("mdi-pencil", -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }, 8, ["onClick"]),
                          _createVNode(_component_v_btn, {
                            icon: "",
                            size: "x-small",
                            variant: "text",
                            color: "error",
                            onClick: ($event: any) => (confirmDelete(r))
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_icon, { size: "18" }, {
                                default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                                  _createTextVNode("mdi-delete", -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }, 8, ["onClick"])
                        ])
                      ]))
                    }), 128))
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_12, [
                _createVNode(_component_v_btn, {
                  variant: "flat",
                  class: "btn-register",
                  onClick: openAdd
                }, {
                  default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                    _createTextVNode("행 추가", -1)
                  ]))]),
                  _: 1
                })
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _createVNode(_component_v_dialog, {
      modelValue: formOpen.value,
      "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((formOpen).value = $event)),
      class: "budget-suga-info-form-dialog",
      "max-width": "480"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "budget-suga-info-form-title" }, {
              default: _withCtx(() => [
                _cache[25] || (_cache[25] = _createElementVNode("span", { class: "budget-admin-badge" }, "관리자전용", -1)),
                _createTextVNode(" " + _toDisplayString(formMode.value === 'add' ? '행 추가' : '행 수정') + " ", 1),
                _createElementVNode("button", {
                  type: "button",
                  class: "dialog-close-x",
                  title: "닫기",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (formOpen.value = false))
                }, [
                  _createVNode(_component_v_icon, { size: "18" }, {
                    default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                      _createTextVNode("mdi-close", -1)
                    ]))]),
                    _: 1
                  })
                ])
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_text, { class: "budget-suga-info-form-body" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_select, {
                  modelValue: form.value.bizGubun,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.value.bizGubun) = $event)),
                  items: bizOptions,
                  label: "사업구분",
                  density: "compact",
                  variant: "outlined",
                  "hide-details": "",
                  class: "budget-suga-info-form-field"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_text_field, {
                  modelValue: form.value.year,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.value.year) = $event)),
                  modelModifiers: { number: true },
                  label: "연도",
                  type: "number",
                  density: "compact",
                  variant: "outlined",
                  "hide-details": "",
                  class: "budget-suga-info-form-field"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_text_field, {
                  modelValue: form.value.rank,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.value.rank) = $event)),
                  label: "구간(등급)",
                  density: "compact",
                  variant: "outlined",
                  "hide-details": "",
                  class: "budget-suga-info-form-field"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_select, {
                  modelValue: form.value.isUse,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.value.isUse) = $event)),
                  items: ['Y', 'N'],
                  label: "사용",
                  density: "compact",
                  variant: "outlined",
                  "hide-details": "",
                  class: "budget-suga-info-form-field"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_select, {
                  modelValue: form.value.basisYn,
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((form.value.basisYn) = $event)),
                  items: ['Y', 'N'],
                  label: "기초",
                  density: "compact",
                  variant: "outlined",
                  "hide-details": "",
                  class: "budget-suga-info-form-field"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_text_field, {
                  modelValue: form.value.grossFee,
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((form.value.grossFee) = $event)),
                  label: "급여 총액(장기요양급여)",
                  density: "compact",
                  variant: "outlined",
                  "hide-details": "",
                  class: "budget-suga-info-form-field"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_text_field, {
                  modelValue: form.value.copayFee,
                  "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((form.value.copayFee) = $event)),
                  label: "본인부담",
                  density: "compact",
                  variant: "outlined",
                  "hide-details": "",
                  class: "budget-suga-info-form-field"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_text_field, {
                  modelValue: form.value.sort,
                  "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((form.value.sort) = $event)),
                  modelModifiers: { number: true },
                  label: "정렬(작을수록 위)",
                  type: "number",
                  density: "compact",
                  variant: "outlined",
                  "hide-details": ""
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  variant: "text",
                  class: "btn-cancel",
                  onClick: _cache[12] || (_cache[12] = ($event: any) => (formOpen.value = false))
                }, {
                  default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                    _createTextVNode("취소", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  variant: "flat",
                  class: "btn-save",
                  loading: formSaving.value,
                  onClick: submitForm
                }, {
                  default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                    _createTextVNode("저장", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})